/**
 * Address search input (issue #23).
 *
 * A text input that queries the Places Autocomplete proxy as you type and
 * shows a dropdown of address suggestions. Selecting one fills the field; free
 * text is still allowed (the value commits on change), so it degrades to a
 * plain input when address search is unconfigured (empty suggestion list).
 *
 * Follows DESIGN-SYSTEM.md: option rows are raw buttons with cursor-pointer, the
 * dropdown sits on a border (no shadow). Dismissal (outside click + Escape)
 * comes from `useDismissable`; the arrow-key/Enter listbox behaviour and its
 * ARIA wiring from `useListboxNav`.
 */
import { useEffect, useRef, useState } from "react";
import { cx } from "@shared/cx";
import formStyles from "./field.module.css";
import { useDebouncedValue } from "../../lib/use-debounced-value";
import { useDismissable } from "../../lib/use-dismissable";
import { useListboxNav } from "../../lib/use-listbox-nav";
import { autocompleteAddress } from "../../lib/places-api";
import styles from "./AddressAutocomplete.module.css";

export function AddressAutocomplete({
  id,
  value,
  onChange,
  ariaLabel,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  ariaLabel?: string;
}) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  // Suppress the fetch triggered by programmatically setting the field after a
  // pick — otherwise selecting a suggestion immediately re-opens the dropdown.
  const justPicked = useRef(false);
  const debounced = useDebouncedValue(value, 250);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (justPicked.current) {
      justPicked.current = false;
      return;
    }
    if (!debounced || debounced.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const ac = new AbortController();
    autocompleteAddress(debounced, ac.signal)
      .then((res) => {
        setSuggestions(res.map((s) => s.description));
        setOpen(true);
      })
      .catch(() => setSuggestions([]));
    return () => ac.abort();
  }, [debounced]);

  useDismissable({
    open,
    onDismiss: () => setOpen(false),
    pointer: [wrapperRef],
  });

  const pick = (s: string) => {
    justPicked.current = true;
    onChange(s);
    setSuggestions([]);
    setOpen(false);
  };

  const nav = useListboxNav({
    open: open && suggestions.length > 0,
    count: suggestions.length,
    onCommit: (i) => pick(suggestions[i]),
  });

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <input
        id={id}
        aria-label={ariaLabel}
        autoComplete="off"
        className={formStyles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        onKeyDown={nav.onKeyDown}
        {...nav.comboboxProps}
      />
      {open && suggestions.length > 0 && (
        <div
          id={nav.listboxId}
          role="listbox"
          aria-label={
            ariaLabel ? `${ariaLabel} suggestions` : "Address suggestions"
          }
          className={styles.listbox}
        >
          {suggestions.map((s, i) => (
            <button
              key={`${s}-${i}`}
              id={nav.optionId(i)}
              type="button"
              role="option"
              aria-selected={i === nav.activeIndex}
              tabIndex={-1}
              className={cx(
                styles.option,
                i === nav.activeIndex && styles.optionActive,
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
